/**
 * Base Model Card Component
 *
 * @package
 */

import { useState, useEffect, RawHTML, memo } from '@wordpress/element';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import ProductInfoModal from './ProductInfoModal';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { formatPrice as formatPriceHelper, getPriceFromProduct } from '../helpers/priceHelpers';
import { cachedApiFetch } from '../helpers/useApiCache';

const BaseModelCard = ( { modelId, description, image, isSelected, onSelect, vatMode } ) => {
	const [ productData, setProductData ] = useState( null );
	const [ showInfoModal, setShowInfoModal ] = useState( false );

	useEffect( () => {
		cachedApiFetch( `/wp/v2/qcom_product/${ modelId }?_embed` )
			.then( data => {
				setProductData( data );
			} )
			.catch( error => {
				// eslint-disable-next-line no-console
				console.error( 'Failed to fetch base model data:', error );
			} );
	}, [ modelId ] );

	if ( ! productData ) {
		return (
			<div className="configurator-base-model-card loading" role="status" aria-live="polite">
				<span className="screen-reader-text">{ __( 'Loading base model information…', 'at-vehicle-configurator' ) }</span>
				<div className="configurator-base-model-card-content">
					<div className="configurator-base-model-card-header">
						<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '70%', height: '22px', flexShrink: 1 } } aria-hidden="true" />
						<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '72px', height: '18px', flexShrink: 0 } } aria-hidden="true" />
					</div>
				</div>
			</div>
		);
	}

	const handleKeyDown = e => {
		if ( e.key === 'Enter' || e.key === ' ' ) {
			e.preventDefault();
			onSelect();
		}
	};

	const modelTitle = decodeHtmlEntities( productData.title?.rendered ) || __( 'Base Model', 'at-vehicle-configurator' );

	const price = getPriceFromProduct( productData, vatMode );
	const formattedPrice = price > 0 ? formatPriceHelper( price ) : null;

	return (
		<>
			<div
				className={ `configurator-base-model-card ${ isSelected ? 'selected' : '' }` }
				role="button"
				tabIndex={ 0 }
				onClick={ onSelect }
				onKeyDown={ handleKeyDown }
				aria-label={ `${ isSelected ? __( 'Selected: ', 'at-vehicle-configurator' ) : __( 'Select ', 'at-vehicle-configurator' ) }${ modelTitle }` }
				aria-pressed={ isSelected }
			>
				{ image && (
					<div className="configurator-base-model-card-image-wrapper">
						{ /* Above the fold at the top of the sidebar, so no lazy loading. */ }
						<img src={ image } alt={ modelTitle } decoding="async" />
					</div>
				) }
				<div className="configurator-base-model-card-content">
					<div className="configurator-base-model-card-header">
						<h3>{ modelTitle }</h3>
						{ formattedPrice && (
							<div className="configurator-base-model-price">{ formattedPrice }</div>
						) }
					</div>
					{ isSelected && description && (
						<div className="configurator-base-model-description">
							<RawHTML>{ description }</RawHTML>
						</div>
					) }
					{ ! isSelected && productData.excerpt?.rendered && (
						<div>
							<RawHTML>{ productData.excerpt.rendered }</RawHTML>
						</div>
					) }
				</div>
			</div>
			<ProductInfoModal
				productId={ modelId }
				isOpen={ showInfoModal }
				onClose={ () => setShowInfoModal( false ) }
				isSelected={ isSelected }
			/>
		</>
	);
};

BaseModelCard.propTypes = {
	modelId: PropTypes.number.isRequired,
	description: PropTypes.string,
	image: PropTypes.string,
	isSelected: PropTypes.bool.isRequired,
	onSelect: PropTypes.func.isRequired,
	vatMode: PropTypes.oneOf( [ 'including', 'excluding' ] ),
};

BaseModelCard.defaultProps = {
	description: null,
	image: null,
	vatMode: 'including',
};

export default memo( BaseModelCard );
